import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { useSendCmpMemberDashboardHelpRequestMutation } from '../../../graphql/types';
import { getUserFacingErrorText } from '../utilities/userFacingError';

// Wave C BE-3 / Member Dashboard design Help modal (ACC:L1354-1385, decision
// 5): the member question goes to the chamber by email. Name and email
// prefill from the signed-in contact; the chamber replies to the email given.
const RED = '#ED1C24';
const TEXT = '#0A0A0A';
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;
export const HELP_REQUIRED_ERROR = 'Please complete all required fields with a valid email.';

type Form = { name: string; email: string; subject: string; message: string };

const inputStyle = (bad: boolean, multiline = false): React.CSSProperties => ({
  border: `1px solid ${bad ? RED : '#CFCFCF'}`, borderRadius: 8, padding: '10px 12px', fontFamily: "'DM Sans', sans-serif",
  fontSize: 14, color: TEXT, width: '100%', boxSizing: 'border-box', outline: 'none', background: '#FFFFFF',
  ...(multiline ? { resize: 'vertical', minHeight: 110, lineHeight: 1.5 } : {}),
});

const Label = ({ htmlFor, text }: { htmlFor: string; text: string }) => (
  <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 13, color: TEXT, marginBottom: 6 }}>
    {text} <span style={{ color: RED }}>*</span>
  </label>
);

export default function CmpMemberHelpModal({ chamberName, defaultName, defaultEmail, onClose }: {
  chamberName: string; defaultName?: string | null; defaultEmail?: string | null; onClose: () => void;
}) {
  const [form, setForm] = useState<Form>({ name: defaultName || '', email: defaultEmail || '', subject: '', message: '' });
  const [showErrors, setShowErrors] = useState(false);
  const [sendError, setSendError] = useState<string>();
  const [sentTo, setSentTo] = useState<string>();
  const [send, { loading }] = useSendCmpMemberDashboardHelpRequestMutation();
  const emailOk = EMAIL_PATTERN.test(form.email.trim());
  const missing = {
    name: !form.name.trim(), email: !emailOk, subject: !form.subject.trim(), message: !form.message.trim(),
  };
  const set = (field: keyof Form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [field]: event.target.value });
    setShowErrors(false);
    setSendError(undefined);
  };
  const submit = async () => {
    if (loading) return;
    if (Object.values(missing).some(Boolean)) { setShowErrors(true); return; }
    setSendError(undefined);
    try {
      const result = await send({ variables: { input: {
        name: form.name.trim(), email: form.email.trim(), subject: form.subject.trim(), message: form.message.trim(),
      } } });
      setSentTo(result.data?.sendCmpMemberDashboardHelpRequest.replyToEmail || form.email.trim());
    } catch (error) {
      setSendError(getUserFacingErrorText(error,
        `We could not send your question to ${chamberName}. Check your internet connection and click Send to Chamber again.`));
    }
  };
  return (
    <Dialog open onClose={onClose} maxWidth={false} aria-labelledby="cmp-member-help-title"
      PaperProps={{ style: { width: 'min(700px, 92vw)', maxHeight: '90vh', borderRadius: 16, margin: 0, position: 'relative',
        boxShadow: '0 8px 30px rgba(0,0,0,.12)', fontFamily: "'DM Sans', sans-serif", color: TEXT } }}>
      <button type="button" aria-label="Close" onClick={onClose}
        style={{ position: 'absolute', top: 14, right: 14, width: 28, height: 28, borderRadius: '50%', background: RED, color: '#FFFFFF',
          border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
      {sentTo ? (
        <div role="status" style={{ padding: '36px 32px 32px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#10B981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFFFFF' }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 12.5l5 5L20 7" /></svg>
          </div>
          <div id="cmp-member-help-title" style={{ fontSize: 22, fontWeight: 700 }}>Request Sent</div>
          <div style={{ fontSize: 14, lineHeight: 1.55, maxWidth: 420 }}>Your request has been sent to {chamberName}. They will reply to <b>{sentTo}</b>.</div>
        </div>
      ) : (
        <>
          <div style={{ padding: '26px 32px 8px' }}>
            <div id="cmp-member-help-title" style={{ fontSize: 22, fontWeight: 700, marginBottom: 6 }}>Need Help?</div>
            <div style={{ fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>Send your question to {chamberName}. A member of the chamber team will reply by email.</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
                <div><Label htmlFor="cmp-help-name" text="Your name" />
                  <input id="cmp-help-name" value={form.name} onChange={set('name')} placeholder="Full name" maxLength={120} style={inputStyle(showErrors && missing.name)} /></div>
                <div><Label htmlFor="cmp-help-email" text="Email" />
                  <input id="cmp-help-email" type="email" value={form.email} onChange={set('email')} placeholder="name@company.com" style={inputStyle(showErrors && missing.email)} /></div>
              </div>
              <div><Label htmlFor="cmp-help-subject" text="Subject" />
                <input id="cmp-help-subject" value={form.subject} onChange={set('subject')} placeholder="What do you need help with?" maxLength={200} style={inputStyle(showErrors && missing.subject)} /></div>
              <div><Label htmlFor="cmp-help-message" text="Message" />
                <textarea id="cmp-help-message" value={form.message} onChange={set('message')} rows={5} placeholder="Describe your question or issue" maxLength={5000} style={inputStyle(showErrors && missing.message, true)} /></div>
              {showErrors && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{HELP_REQUIRED_ERROR}</div>}
              {sendError && <div role="alert" style={{ fontSize: 13, fontWeight: 600, color: RED }}>{sendError}</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, padding: '20px 32px 24px' }}>
            <button type="button" onClick={submit} disabled={loading}
              style={{ flex: 1, background: '#E1A731', color: '#FFFFFF', border: 'none', borderRadius: 8, padding: '13px 0', fontFamily: "'DM Sans', sans-serif",
                fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Sending…' : 'Send to Chamber'}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}
